import { useContext, useEffect, useState, useMemo } from 'react'
import GlobalContext from '../../../context/global-context'
import { TablesAPI } from '../../../api/table.api'
import { BusinessAPI } from '../../../api/business.api'
import TableBalance from './Table'
import { HeadingTitle, InputCheckBox } from '../../../components/modules/ui/index'
import CustomButton from '../../../components/modules/ui/CustomButton'
import FilterDateControl from '../../../components/modules/table/FilterDateControl'
import dayjs from 'dayjs'
import ExportToXlsxModal from '../../../components/modules/modals/ExportToXlsxModal'

const NUM_FIELDS = [
  'debe','haber','deudor','acreedor','activo','pasivo','perdida','ganancia'
]
const fmtNum = v => Number(v ?? 0)

// Helper: excluir códigos cuya última sección sea "0"
const isTerminalZeroCode = (code) => {
  if (!code) return false
  const last = String(code).trim().split('-').pop()
  return last === '0'
}

const ListBalanceGeneral = () => {
  const [data, setData] = useState([])
  const [cuentasContables, setCuentasContables] = useState([])

  const { ui, currentBusiness } = useContext(GlobalContext)
  const [filters, setFilters] = useState({
    fechaInicio: dayjs().startOf('year').format('YYYY-MM-DD'),
    fechaFin:    dayjs().format('YYYY-MM-DD'),
  })
  const [tempFilters, setTempFilters] = useState(filters)
  const [isExportModalOpen, setIsExportModalOpen] = useState(false)
  const [isExporting, setIsExporting] = useState(false)

  // controla "mostrar todas"
  const [showAll, setShowAll] = useState(false)

  const tablesAPI = useMemo(() => new TablesAPI(), [])
  const businessAPI = useMemo(() => new BusinessAPI(), [])

  const getData = async (updatedFilters) => {
    if (!currentBusiness?.id) {
      console.error('No hay un negocio seleccionado')
      setData([])
      setCuentasContables([])
      return
    }
    ui.setLoader({ visible: true, text: 'Cargando los datos de la tabla...' })
    try {
      const { fechaInicio, fechaFin } = updatedFilters
      const [balanceRes, planRes] = await Promise.all([
        tablesAPI.getDataBalance(currentBusiness.id, fechaInicio, fechaFin),
        tablesAPI.getCuentaContable(currentBusiness.id),
      ])
      setData(balanceRes?.data ?? [])
      setCuentasContables(Array.isArray(planRes) ? planRes : [])
    } catch (error) {
      console.error(error)
      setData([])
      setCuentasContables([])
    } finally {
      ui.setLoader({ visible: false, text: '' })
    }
  }

  useEffect(() => {
    getData(filters)
  }, [currentBusiness])

  const handleSearch = () => {
    setFilters(tempFilters)
    getData(tempFilters)
  }

  // ---------- Cruce Plan de Cuentas ↔ Balance ----------
  // 1) Separo filas de datos (con numCuenta) vs. resúmenes (sin numCuenta) y excluyo *-0
  const dataRows = useMemo(
    () => (Array.isArray(data)
      ? data.filter(r => r?.numCuenta && !isTerminalZeroCode(r.numCuenta))
      : []
    ),
    [data]
  )

  const summaryRows = useMemo(
    () => (Array.isArray(data) ? data.filter(r => !r?.numCuenta) : []),
    [data]
  )

  // 2) Normalizo filas del plan (excluyendo *-0) para calzar con Table
  const planRows = useMemo(() => {
    return (cuentasContables ?? [])
      .filter(c => !isTerminalZeroCode(c.codigoFinal))
      .map(c => ({
        numCuenta:  c.codigoFinal,
        nameCuenta: c.nameFinal,
        debe: 0, haber: 0, deudor: 0, acreedor: 0,
        activo: 0, pasivo: 0, perdida: 0, ganancia: 0,
      }))
  }, [cuentasContables])

  // 3) Merge: datos + plan (evitar duplicados por numCuenta) + resúmenes
  const mergedAllRows = useMemo(() => {
    const seen = new Set(dataRows.map(r => String(r.numCuenta)))
    const missingFromPlan = planRows.filter(p => !seen.has(String(p.numCuenta)))
    return [...dataRows, ...missingFromPlan, ...summaryRows]
  }, [dataRows, planRows, summaryRows])

  // 4) Filas “con datos” = alguna columna numérica distinta de 0
  const rowsWithData = useMemo(() => {
    return dataRows.filter(r => NUM_FIELDS.some(k => fmtNum(r[k]) !== 0))
  }, [dataRows])

  // 5) Decidir qué mostrar según el checkbox
  const rowsToShow = useMemo(() => {
    if (showAll) return mergedAllRows
    const base = rowsWithData.length ? rowsWithData : mergedAllRows
    // incluir resúmenes si estás mostrando solo con datos
    return base === mergedAllRows ? base : [...rowsWithData, ...summaryRows]
  }, [showAll, mergedAllRows, rowsWithData, summaryRows])

  const handleExport = async (email) => {
    if (!currentBusiness?.id) return
    setIsExporting(true)
    try {
      await businessAPI.exportBalanceGeneral({
        businessId: currentBusiness.id,
        email,
        from: filters.fechaInicio,
        to: filters.fechaFin
      })
    } catch (error) {
      console.error('Error al exportar balance general:', error)
      throw error
    } finally {
      setIsExporting(false)
    }
  }

  const icons = useMemo(() => [
    <button
      key='export-xlsx'
      className='w-10 h-10 text-green-700 font-bold cursor-pointer rounded-full shadow bg-white'
      onClick={() => setIsExportModalOpen(true)}
      disabled={!currentBusiness?.id}
    >
      XLS
    </button>
  ], [currentBusiness?.id])

  return (
    <>
      <HeadingTitle title='Balance General' rightIcons={icons} iconHeight={32} />

      <div className='p-4'>
        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end'>
          <FilterDateControl
            label='Fecha Desde'
            value={tempFilters.fechaInicio}
            onChange={newValue => {
              const y = dayjs(newValue).year()
              setTempFilters({
                ...tempFilters,
                fechaInicio: dayjs(newValue).format('YYYY-MM-DD'),
                fechaFin:
                  dayjs(tempFilters.fechaFin).year() === y
                    ? tempFilters.fechaFin
                    : dayjs(newValue).endOf('year').format('YYYY-MM-DD'),
              })
            }}
          />

          <FilterDateControl
            label='Fecha Hasta'
            value={tempFilters.fechaFin}
            onChange={newValue => {
              const y = dayjs(newValue).year()
              setTempFilters({
                ...tempFilters,
                fechaFin: dayjs(newValue).format('YYYY-MM-DD'),
                fechaInicio:
                  dayjs(tempFilters.fechaInicio).year() === y
                    ? tempFilters.fechaInicio
                    : dayjs(newValue).startOf('year').format('YYYY-MM-DD'),
              })
            }}
          />

          <InputCheckBox
            label='Mostrar todas las cuentas'
            checked={showAll}
            onChange={() => setShowAll(v => !v)}
            classNameConteiner='flex items-center gap-2 h-8'
            className='h-4 w-4'
          />

          <CustomButton
            className='rounded-full h-8 w-full'
            color='primary'
            title='Buscar'
            onClick={handleSearch}
          />
        </div>
      </div>

      <TableBalance rows={rowsToShow} getData={getData} />
      <ExportToXlsxModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        onExport={handleExport}
        title='Exportar Balance General'
        description='Se enviará un archivo Excel con el balance general al email especificado'
        isLoading={isExporting}
      />
    </>
  )
}

export default ListBalanceGeneral
